/**
 * KB-3733 — the ONE mapping from a stored offer to the price/length inputs of `prepareOfferDataCore`,
 * and the rule for when a rebuild must use it instead of the catalog variation.
 *
 * THE DEFECT. Every seam that rebuilds an EXISTING offer (`publishOffersForLocation`, the shared-program
 * sync, the collision re-key, and the dashboard's single-offer / batch saves) built its core form from
 * the stored offer's overrides only, so `priceRegular`, `priceCalc`, the tiers and `numDaysCalc`
 * re-resolved from the catalog variation. Executed on the emulator: a camp-data week observed at $300
 * came back at the catalog's $275, and sonshine's approved two-day $130 came back at $60 for ONE day —
 * undoing KB-3436 and KB-2866 on any Publish or any description edit.
 *
 * THE RULE (Nicholas, KB-3733 comment 4761a968):
 *   1. A session with its OWN price keeps it — price, tiers and length — on every rebuild.
 *   2. A session known to belong to the program with no observed price of its own takes the program
 *      price as its default, and a rebuild may refresh it.
 *   Absent evidence falls to rule 1, never to the program price.
 *
 * THE CARRIER. Nothing on an offer doc records whether its price was observed or catalog-filled
 * (0 of 7,685 camp-data offers, 2026-09-23). The evidence that a camp-data session was program-defaulted
 * lives only in its creating run's GCS audit, which a rebuild cannot read — and since KB-3639 the
 * writer refuses to CREATE such a session at all. So:
 *   - a camp-data offer (`sourceKey` "obs:…") is rule 1, always — including a stored null (unknown stays
 *     unknown; it never falls through to the catalog) and a stored $0 (resolves to unknown, KB-3726);
 *   - any other offer is rule 2 — today's catalog behaviour, which is correct for a hand-curated session
 *     that never had a price of its own.
 * A camp-data offer can only move to rule 2 through a positive carrier stamped by whatever PROVES it was
 * program-defaulted (KB-3753's audit replay). Add that condition in the same change as the stamp.
 *
 * KB-3769 — that carrier is `priceSource: "program-default"`, stamped by the camp-data create when the
 * session stated no price and was created at its program's (non-zero) default, and cleared when an
 * observed or approved price lands. Such an offer's PRICE takes rule 2 (a rebuild refreshes it from the
 * program); its LENGTH is still the session's own, so the catalog pass keeps resolving against it.
 *
 * Pure: no Firestore, no clock — imported by the functions seams AND the web client (`@/functions-shared`).
 */
import {
  prepareOfferDataCore,
  priceBucketFloor,
  PRICE_PER_DAY_BUCKET_BIN_SIZE,
  type CoreInput,
  type CoreOutput,
} from "./prepareOfferDataCore";

/** The `sourceKey` prefix the camp-data writer stamps on every offer it creates (data-model invariant 14). */
export const CAMP_DATA_SOURCE_KEY_PREFIX = "obs:";

export function isCampDataOffer(offer: Record<string, unknown> | null | undefined): boolean {
  const k = offer?.sourceKey;
  return typeof k === "string" && k.startsWith(CAMP_DATA_SOURCE_KEY_PREFIX);
}

/** KB-3769 — the carrier a camp-data create stamps when the session's price IS its program's default. */
export const PROGRAM_DEFAULT_PRICE_SOURCE = "program-default";

/** A camp-data offer whose price was its program's default (rule 2 for price), not observed (rule 1). */
export function isProgramDefaultPricedOffer(offer: Record<string, unknown> | null | undefined): boolean {
  return isCampDataOffer(offer) && offer?.priceSource === PROGRAM_DEFAULT_PRICE_SOURCE;
}

/**
 * KB-3769 — does a write of `fields` land the session's OWN price on a program-default offer? An approved
 * or staff-corrected `priceRegular` is a price someone vouched for, not the program's default any more, so
 * the caller deletes the label in the same write and the offer returns to rule 1.
 */
export function landsOwnPriceOnProgramDefault(stored: Record<string, unknown> | null | undefined, fields: Iterable<string>): boolean {
  if (!isProgramDefaultPricedOffer(stored)) return false;
  for (const f of fields) if (String(f) === "priceRegular") return true;
  return false;
}

/** Rule 1 for price: a camp-data offer that has not been positively labelled program-defaulted. */
function keepsOwnPrice(offer: Record<string, unknown> | null | undefined): boolean {
  return isCampDataOffer(offer) && !isProgramDefaultPricedOffer(offer);
}

/** Exported so every reader judges "a finite number" the same way. */
export function finiteOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function toDate(v: unknown): Date {
  if (v instanceof Date) return v;
  if (v && typeof (v as { toDate?: unknown }).toDate === "function") return (v as { toDate: () => Date }).toDate();
  if (typeof v === "string" || typeof v === "number") return new Date(v);
  return new Date(NaN);
}

/**
 * What a rebuild is about to WRITE — the values that come from the edit/seam, not from the stored doc.
 * Dates, the overrides and the sale belong to the writer; the session's observed facts belong to the
 * stored doc. Keeping the two apart is what lets a curator clear an override without the cleared value
 * surviving as a fake "observation".
 */
export interface RebuildWrite {
  startDate?: unknown;
  endDate?: unknown;
  numDaysOverride?: unknown;
  priceOverride?: unknown;
  currentSalePrice?: unknown;
  pricingTierPriceOverrides?: unknown;
}

function tierOverride(pt: unknown, tier: "tier2" | "tier3"): number | null {
  return pt && typeof pt === "object" ? finiteOrNull((pt as Record<string, unknown>)[tier]) : null;
}

/** Same calendar day in the reader's local accessors — the convention each producer resolves correctly
 *  (noon-UTC camp-data dates and local-midnight dashboard dates land on the same day; see
 *  `prepareOfferDataCore`'s KB-2866 accessor note). */
function sameDay(a: unknown, b: unknown): boolean {
  const x = toDate(a);
  const y = toDate(b);
  if (Number.isNaN(x.getTime()) || Number.isNaN(y.getTime())) return false;
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

/**
 * The number of camp days (Mon–Fri) in an inclusive span, read with local accessors like `sameDay`;
 * `null` for an unreadable or inverted span, or one with no weekday at all (a weekend-only camp, whose
 * length the core then takes from the calendar span). Used when the stored length no longer describes
 * the dates being written, so BOTH core passes resolve against the same camp-day count rather than one
 * counting weekends and the other using the catalog's representative length.
 */
function weekdaySpan(start: unknown, end: unknown): number | null {
  const s = toDate(start);
  const e = toDate(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const DAY_MS = 86400000;
  const first = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
  const last = new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime();
  if (last < first || (last - first) / DAY_MS > 366) return null;
  let days = 0;
  for (let t = first; t <= last; t += DAY_MS) {
    const dow = new Date(t).getDay();
    if (dow !== 0 && dow !== 6) days += 1;
  }
  return days > 0 ? days : null;
}

/** Inclusive calendar days of a span (local accessors), or null when unreadable/inverted. */
function calendarSpan(start: unknown, end: unknown): number | null {
  const s = toDate(start);
  const e = toDate(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const first = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
  const last = new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime();
  if (last < first) return null;
  return Math.round((last - first) / 86400000) + 1;
}

/**
 * The length of the span being WRITTEN, counted the way THIS camp counts days. A session whose stored
 * length equals its own calendar span while that span includes a weekend (a Sun–Sat overnight week)
 * counts every day; everything else counts Mon–Fri camp days (a two-week day camp is 10, not 12).
 * Falls back to the calendar span when the new span has no weekday.
 */
function lengthOfWrittenSpan(stored: Record<string, unknown>, write: RebuildWrite): number | null {
  const storedDays = finiteOrNull(stored.numDaysCalc);
  const storedCal = calendarSpan(stored.startDate, stored.endDate);
  const countsEveryDay =
    storedDays != null && storedCal != null && storedDays === storedCal && storedCal !== weekdaySpan(stored.startDate, stored.endDate);
  if (countsEveryDay) return calendarSpan(write.startDate, write.endDate);
  return weekdaySpan(write.startDate, write.endDate) ?? calendarSpan(write.startDate, write.endDate);
}

/**
 * The session's OWN observed values, read from the stored (pre-edit) doc, as core `observed*` inputs.
 *
 * Each stored value is a DERIVED field, so each is trusted only where nothing else produced it:
 *   - `priceRegular` — overrides and sales move `priceCalc`, never the list price, so it is the session's.
 *   - `numDaysCalc` — only when the stored doc had no `numDaysOverride` (else it IS the override, and a
 *     cleared override would survive as an "observation"), and only when the write keeps the dates (a
 *     length observed for one span says nothing about another). Otherwise the camp days (Mon–Fri) of the
 *     span being written, fed to both passes so the length and the matched increment alternative agree.
 *   - `priceTier2Calc`/`priceTier3Calc` — only when that tier had no stored override, for the same reason.
 * A withheld length stays withheld whatever the dates (KB-2877). A stored null that is NOT a withhold is
 * never promoted to one — that verdict carries meaning (data-model §1d) and only its producer may stamp it.
 */
export function sessionObservedInputs(stored: Record<string, unknown>, write: RebuildWrite): Partial<CoreInput["form"]> {
  const o = stored || {};
  const storedNumDays = finiteOrNull(o.numDaysCalc);
  const lengthIsTheSessions =
    finiteOrNull(o.numDaysOverride) == null && sameDay(o.startDate, write.startDate) && sameDay(o.endDate, write.endDate);
  const storedTiers = o.pricingTierPriceOverrides;
  return {
    observedPriceRegular: finiteOrNull(o.priceRegular),
    // A withhold outranks every derived length: the core reads `observedNumDays` BEFORE `numDaysUnknown`,
    // so a span count here would silently un-withhold the session (KB-2877). Otherwise the stored length
    // when it is the session's, else the written span counted this camp's way — in BOTH passes, so the
    // matched increment alternative and the written length always agree.
    observedNumDays:
      o.numDaysState === "withheld"
        ? null
        : lengthIsTheSessions && storedNumDays != null && storedNumDays >= 1
          ? storedNumDays
          : lengthOfWrittenSpan(o, write),
    numDaysUnknown: o.numDaysState === "withheld",
    observedPriceTier2: tierOverride(storedTiers, "tier2") == null ? finiteOrNull(o.priceTier2Calc) : null,
    observedPriceTier3: tierOverride(storedTiers, "tier3") == null ? finiteOrNull(o.priceTier3Calc) : null,
  };
}

/** The write a stored doc implies about itself — a rebuild that changes nothing. */
function writeFromStored(o: Record<string, unknown>): RebuildWrite {
  return {
    startDate: o.startDate,
    endDate: o.endDate,
    numDaysOverride: o.numDaysOverride,
    priceOverride: o.priceOverride,
    currentSalePrice: o.currentSalePrice,
    pricingTierPriceOverrides: o.pricingTierPriceOverrides,
  };
}

/**
 * The full core form for resolving `stored`'s price as it will be after `write`: the session's observed
 * values (`sessionObservedInputs`), the write's dates/overrides/sale, and the curated `priceState` fed
 * back (KB-2103 — a curator's "free" survives). `write` defaults to the stored doc's own values, which is
 * the shape `recomputeDerivedPriceFields` needs (its caller has already merged the patch in).
 */
export function storedSessionPriceInputs(stored: Record<string, unknown>, write?: RebuildWrite): Partial<CoreInput["form"]> {
  const o = stored || {};
  const w = write ?? writeFromStored(o);
  return {
    startDate: toDate(w.startDate),
    endDate: toDate(w.endDate),
    numDaysOverride: finiteOrNull(w.numDaysOverride),
    priceOverride: finiteOrNull(w.priceOverride),
    currentSalePrice: finiteOrNull(w.currentSalePrice),
    pricingTierPriceOverrides: (w.pricingTierPriceOverrides as any) ?? null,
    priceState: typeof o.priceState === "string" ? o.priceState : null,
    ...sessionObservedInputs(o, w),
  };
}

/**
 * The observed inputs to add to a seam's CATALOG core call for a camp-data offer, so the catalog pass
 * resolves against the session's length and price too. Without it the catalog pass matches an increment
 * alternative (and its price note, and its `regularOfferIncrementKey`) for the CATALOG length and injects
 * them into `variationData`, beside a session length the overlay then writes — an internally inconsistent
 * doc. Empty for every other offer.
 *
 * KB-3769 — a program-defaulted camp-data offer contributes its LENGTH only: its stored price is the
 * program's, so passing it as "observed" would freeze the very number rule 2 lets a rebuild refresh.
 */
export function campDataCatalogInputs(stored: Record<string, unknown> | null, write: RebuildWrite): Partial<CoreInput["form"]> {
  if (!stored || !isCampDataOffer(stored)) return {};
  const observed = sessionObservedInputs(stored, write);
  if (!isProgramDefaultPricedOffer(stored)) return observed;
  return { observedNumDays: observed.observedNumDays, numDaysUnknown: observed.numDaysUnknown };
}

/**
 * The variation with every PRICE input removed — no `priceRegular`, no tiers, no increment alternatives,
 * no representative `numDays` — so the core cannot fall through to a catalog number when the session's
 * own value is null. `pricingTiers` is kept: it is a declaration of how many tiers the card shows, not a
 * price, and the core's ascending-tier anchor reads it.
 */
export function sessionPriceVariation(variation: { key?: unknown; pricingTiers?: unknown } | null | undefined): CoreInput["variation"] {
  const v: Record<string, unknown> = { key: String(variation?.key ?? "") };
  if (variation?.pricingTiers != null) v.pricingTiers = variation.pricingTiers;
  return v as unknown as CoreInput["variation"];
}

/**
 * The core fields a rule-1 rebuild takes from the session rather than the catalog. `numDaysState` is
 * deliberately NOT here: it is a verdict, not a derived value, and the catalog pass already carries the
 * stored withhold through; taking it from the session pass would let a stored null be re-stamped.
 */
export type SessionPricedFields = Pick<
  CoreOutput,
  | "numDaysCalc"
  | "priceCalc"
  | "pricePerDayCalc"
  | "priceBucket"
  | "pricePerDayBucket"
  | "priceRegular"
  | "priceState"
  | "priceTier2Calc"
  | "priceTier3Calc"
  | "priceTierWithheld"
>;

/**
 * Rule 1: resolve an offer's price/length fields from its OWN stored values, through the same core the
 * publish path uses (precedence, price-state normalisation and bucket floors all stay in the core).
 *
 * `pricePerDayCalc` is appliable with its own verdicts (KB-2877), so a stored value is kept when — and
 * only when — neither of its inputs moved: the session is priced and this rebuild resolves the SAME
 * `priceCalc` and `numDaysCalc` as the stored doc. When either moves (a new override, a sale, a date or
 * length change) the stored per-day describes another price and is re-derived, or it would sit in the
 * wrong per-day filter bucket forever. Its bucket is floored from whatever value stands.
 */
export function resolveSessionPricedFields(
  stored: Record<string, unknown>,
  variation: { key?: unknown; pricingTiers?: unknown } | null | undefined,
  write?: RebuildWrite,
): SessionPricedFields {
  const core = prepareOfferDataCore({
    location: { id: "", name: "" },
    variation: sessionPriceVariation(variation ?? { key: stored?.variationKey }),
    decimalEnums: {},
    form: storedSessionPriceInputs(stored, write) as CoreInput["form"],
  });
  const storedPerDay = finiteOrNull(stored?.pricePerDayCalc);
  const inputsUnchanged =
    core.priceState == null &&
    core.priceCalc === finiteOrNull(stored?.priceCalc) &&
    core.numDaysCalc === finiteOrNull(stored?.numDaysCalc);
  const pricePerDayCalc = inputsUnchanged && storedPerDay != null ? storedPerDay : core.pricePerDayCalc;
  return {
    numDaysCalc: core.numDaysCalc,
    priceCalc: core.priceCalc,
    pricePerDayCalc,
    priceBucket: core.priceBucket,
    pricePerDayBucket: priceBucketFloor(pricePerDayCalc, PRICE_PER_DAY_BUCKET_BIN_SIZE),
    priceRegular: core.priceRegular,
    priceState: core.priceState,
    priceTier2Calc: core.priceTier2Calc,
    priceTier3Calc: core.priceTier3Calc,
    priceTierWithheld: core.priceTierWithheld,
  };
}

/**
 * The price/length overrides a rebuild seam should carry into its form (and so write), given the
 * seam's `normalizeSessionOverrideFieldsForPublish` output.
 *
 * That normaliser drops an override EQUAL to the program value as "not really an override", which is
 * right when the program price is the baseline (rule 2). Under rule 1 the baseline is the session's own
 * price, so a camp-data `priceOverride` of $275 against an observed $300 is a deliberate discount even
 * though the program also says $275 — and dropping it would write `priceOverride: null` beside a
 * `priceCalc` resolved from the override. So a camp-data offer keeps its stored price/length overrides
 * verbatim; notes, timing and the increment key stay normalised.
 */
export function overridesForRebuild<T extends object>(storedOffer: Record<string, unknown>, normalized: T): T {
  // KB-3769 (review round 2, finding 1) — a program-default offer's PRICE baseline is the program (the normaliser's
  // answer stands for price and tiers), but its LENGTH is still the session's: a stored length override equal to
  // the program's length is a real fix to the session, not a redundant echo, so it is kept.
  if (isProgramDefaultPricedOffer(storedOffer)) {
    const n = normalized as Record<string, unknown>;
    return { ...normalized, numDaysOverride: n.numDaysOverride ?? finiteOrNull(storedOffer.numDaysOverride) };
  }
  if (!keepsOwnPrice(storedOffer)) return normalized;
  // Only undo the one thing the normaliser gets wrong under rule 1 — dropping a STORED finite override for
  // equalling the program. Every other normalisation (shape cleanup, passing a legacy value through) stands.
  const n = normalized as Record<string, unknown>;
  const nTiers = n.pricingTierPriceOverrides;
  const tier2 = tierOverride(nTiers, "tier2") ?? tierOverride(storedOffer.pricingTierPriceOverrides, "tier2");
  const tier3 = tierOverride(nTiers, "tier3") ?? tierOverride(storedOffer.pricingTierPriceOverrides, "tier3");
  return {
    ...normalized,
    numDaysOverride: n.numDaysOverride ?? finiteOrNull(storedOffer.numDaysOverride),
    priceOverride: n.priceOverride ?? finiteOrNull(storedOffer.priceOverride),
    pricingTierPriceOverrides:
      tier2 == null && tier3 == null
        ? nTiers ?? null
        : { v: 1, ...(tier2 != null ? { tier2 } : {}), ...(tier3 != null ? { tier3 } : {}) },
  };
}

/**
 * The session-fact fields an explicit edit may set (a history restore, a curated state). Exported so the
 * dialog can make sure it never SENDS them for a camp-data session: its values are a snapshot from when
 * it opened (and `numDaysCalc` a catalog fallback), and sending them would overwrite fresher facts.
 */
export const SESSION_FACT_FIELDS =["priceRegular", "numDaysCalc", "priceTier2Calc", "priceTier3Calc", "priceState"] as const;

/**
 * The stored doc with any session fact the EDIT explicitly sets laid over it — what `updateOffer` should
 * treat as the session's own values. `EntityHistoryPanel` restores a field by `updateOffer(patch)`, so a
 * restored `priceRegular` must land; the dialog sends the stored value for these on a camp-data session
 * (`prepareSubmissionValues`), so it changes nothing. A key the edit does not carry, or carries as
 * `undefined`, leaves the stored value alone.
 */
export function withSessionFactEdits(stored: Record<string, unknown>, edit: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...stored };
  for (const k of SESSION_FACT_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(edit || {}, k) && edit[k] !== undefined) out[k] = edit[k];
  }
  // KB-3769 — a staff edit that sets a different list price on a program-default offer makes that price the
  // session's own: drop the label so the rebuild keeps it (rule 1). The caller deletes it from the doc too.
  if (staffEditLandsOwnPrice(stored, edit)) delete out.priceSource;
  return out;
}

/**
 * KB-3769 (review round 1, finding 4) — does an explicit edit (a history restore through `updateOffer`) set a
 * DIFFERENT `priceRegular` on a program-default offer? Then the price is someone's deliberate value, not the
 * program's default, and the `priceSource` label must go in the same write.
 */
export function staffEditLandsOwnPrice(stored: Record<string, unknown> | null | undefined, edit: Record<string, unknown> | null | undefined): boolean {
  if (!isProgramDefaultPricedOffer(stored) || !edit) return false;
  if (!Object.prototype.hasOwnProperty.call(edit, "priceRegular") || edit.priceRegular === undefined) return false;
  return edit.priceRegular !== stored?.priceRegular;
}

/**
 * The single decision every rebuild seam makes: given the core's catalog-resolved output and the offer
 * being rebuilt, return the output the seam should write. Rule 1 (camp-data) overlays the session-priced
 * fields; rule 2 (everything else, every create — `storedOffer` null — and, KB-3769, a camp-data offer
 * labelled `priceSource: "program-default"`) returns the catalog output untouched.
 */
export function applyStoredSessionPrice<T extends CoreOutput>(
  catalogCore: T,
  storedOffer: Record<string, unknown> | null,
  variation: { key?: unknown; pricingTiers?: unknown },
  write: RebuildWrite,
): T {
  if (!storedOffer || !keepsOwnPrice(storedOffer)) return catalogCore;
  return { ...catalogCore, ...resolveSessionPricedFields(storedOffer, variation, write) };
}
